import { FaCalculator, FaPlus, FaSearch, FaSpinner, FaTrashAlt } from 'react-icons/fa';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

const Api8Form = ({ state, updateState }) => {
    const formData = state.formData || {};
    const wasteEntries = state.wasteEntries || [];
    const inputMethod = state.inputMethod || 'quantity';
    const CLIMATIQ_API_KEY = process.env.REACT_APP_CLIMATIQ_API_KEY || 'Y2ZBNWZKRzRGNzJYNjRQMzpa';

    const wasteCategories = {
        nonHazardous: ['Mixed municipal', 'Paper/cardboard', 'Plastics', 'Metals', 'Glass', 'Wood', 'Organics/food', 'Construction & demolition (C&D)', 'Sludge', 'E-waste (non-hazard)', 'Other non-hazardous'],
        hazardous: ['Solvents', 'Waste oils', 'Acids/alkalis', 'Contaminated rags/absorbents', 'Batteries', 'Pesticide containers', 'Other hazardous']
    };

    const treatmentMethods = ['Landfill (managed)', 'Landfill (unmanaged)', 'Landfill (with gas capture)', 'Incineration (no energy recovery)', 'Incineration (with energy recovery)', 'Recycling', 'Composting', 'Anaerobic digestion', 'Hazardous treatment', 'Other'];
    const containerSizes = ['120-L bin', '240-L bin', '360-L bin', '660-L bin', '1-yd³ skip', '3-yd³ skip', '6-yd³ skip', '8-yd³ skip', '10-yd³ skip', '15-yd³ skip', '20-yd³ skip', '30-m³ roll-off', '40-m³ roll-off', 'Other'];
    const fillLevels = [{ value: 0.25, label: '25%' }, { value: 0.5, label: '50%' }, { value: 0.75, label: '75%' }, { value: 1, label: '100%' }];

    const defaultWasteEmissionFactors = {
        'Mixed municipal': { landfill: 800, recycling: -300, incineration: 400, composting: 50 },
        'Paper/cardboard': { landfill: 600, recycling: -500, incineration: 800, composting: 100 },
        'Plastics': { landfill: 1200, recycling: -800, incineration: 1500, composting: 0 },
        'Metals': { landfill: 200, recycling: -1500, incineration: 100, composting: 0 },
        'Glass': { landfill: 300, recycling: -200, incineration: 100, composting: 0 },
        'Wood': { landfill: 400, recycling: -300, incineration: 800, composting: 50 },
        'Organics/food': { landfill: 1200, recycling: 0, incineration: 600, composting: -200 },
        'Construction & demolition (C&D)': { landfill: 100, recycling: -150, incineration: 50, composting: 0 },
        'Sludge': { landfill: 800, recycling: 0, incineration: 400, composting: -100 },
        'E-waste (non-hazard)': { landfill: 200, recycling: -800, incineration: 100, composting: 0 },
        'Solvents': { landfill: 1500, recycling: -500, incineration: 800, hazardous: 1000 },
        'Waste oils': { landfill: 1200, recycling: -600, incineration: 700, hazardous: 800 },
        'Acids/alkalis': { landfill: 800, recycling: -200, incineration: 400, hazardous: 600 },
        'Batteries': { landfill: 500, recycling: -1000, incineration: 300, hazardous: 400 },
        'Other': { landfill: 600, recycling: -200, incineration: 300, composting: 50 }
    };

    const containerConversion = {
        '120-L bin': 0.08, '240-L bin': 0.16, '360-L bin': 0.24, '660-L bin': 0.44,
        '1-yd³ skip': 0.8, '3-yd³ skip': 2.4, '6-yd³ skip': 4.8, '8-yd³ skip': 6.4,
        '10-yd³ skip': 8.0, '15-yd³ skip': 12.0, '20-yd³ skip': 16.0,
        '30-m³ roll-off': 24.0, '40-m³ roll-off': 32.0, 'Other': 1.0
    };

    const wasteSearchConfig = {
        'Mixed municipal': { query: 'municipal solid waste landfill', preferred_unit_type: 'Mass' },
        'Paper/cardboard': { query: 'paper waste recycling', preferred_unit_type: 'Mass' },
        'Plastics': { query: 'plastic waste landfill', preferred_unit_type: 'Mass' },
        'Metals': { query: 'metal scrap recycling', preferred_unit_type: 'Mass' },
        'Glass': { query: 'glass waste recycling', preferred_unit_type: 'Mass' },
        'Wood': { query: 'wood waste landfill', preferred_unit_type: 'Mass' },
        'Organics/food': { query: 'food waste composting', preferred_unit_type: 'Mass' },
        'Construction & demolition (C&D)': { query: 'construction waste landfill', preferred_unit_type: 'Mass' },
        'Sludge': { query: 'sludge treatment', preferred_unit_type: 'Mass' },
        'E-waste (non-hazard)': { query: 'electronic waste recycling', preferred_unit_type: 'Mass' },
        'Solvents': { query: 'solvent waste treatment', preferred_unit_type: 'Mass' },
        'Waste oils': { query: 'waste oil incineration', preferred_unit_type: 'Mass' },
        'Acids/alkalis': { query: 'acid waste treatment', preferred_unit_type: 'Mass' },
        'Batteries': { query: 'battery waste recycling', preferred_unit_type: 'Mass' },
        'Other': { query: 'waste treatment', preferred_unit_type: 'Mass' }
    };

    const findBestWasteFactor = (results, config) => {
        if (!results || results.length === 0) return null;
        const priorityOrder = [
            factor => factor.unit_type === 'Mass' && factor.name.toLowerCase().includes('waste'),
            factor => factor.unit_type === config.preferred_unit_type,
            factor => factor.category === 'Waste',
            () => true
        ];
        for (const priorityFn of priorityOrder) {
            const factor = results.find(priorityFn);
            if (factor) return factor;
        }
        return results[0];
    };

    const searchEmissionFactors = async (wasteType, treatmentMethod) => {
        try {
            const config = wasteSearchConfig[wasteType];
            if (!config) return null;

            const params = new URLSearchParams({
                query: `${config.query} ${treatmentMethod.toLowerCase()}`,
                data_version: '^3',
                region: 'US',
                year: formData.year || new Date().getFullYear().toString(),
                results_per_page: '10'
            });

            const response = await fetch(`https://api.climatiq.io/data/v1/search?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) return null;
            const data = await response.json();

            if (data.results && data.results.length > 0) {
                const bestFactor = findBestWasteFactor(data.results, config);
                if (bestFactor) {
                    const factorKey = `${wasteType}_${treatmentMethod}`;
                    updateState({ availableFactors: { ...state.availableFactors, [factorKey]: bestFactor } });
                    return bestFactor;
                }
            }

            return null;
        } catch (error) {
            console.error(`Error searching emission factors for ${wasteType}:`, error);
            return null;
        }
    };

    const handleSearchFactors = async () => {
        if (wasteEntries.length === 0) {
            toastr.error('Please add at least one waste entry first');
            return;
        }

        updateState({ isSearching: true, availableFactors: {} });
        toastr.info('Searching for waste emission factors...');

        try {
            const searchPromises = wasteEntries.map(entry =>
                searchEmissionFactors(entry.wasteType, entry.treatmentMethod)
            );
            await Promise.allSettled(searchPromises);
            toastr.success('Factor search completed! Click Calculate to proceed.');
        } catch (error) {
            toastr.error('Search failed. Using default waste factors.');
        } finally {
            updateState({ isSearching: false });
        }
    };

    const calculateQuantityFromBins = (entry) => {
        if (!entry.containerSize || !entry.collectionsPerYear) return 0;
        const baseWeight = containerConversion[entry.containerSize] || 1;
        const fillLevel = entry.fillLevel || 1;
        const collections = parseInt(entry.collectionsPerYear) || 0;
        return baseWeight * fillLevel * collections;
    };

    const getTreatmentKey = (treatmentMethod) => {
        if (treatmentMethod.includes('Landfill')) return 'landfill';
        if (treatmentMethod.includes('Recycling')) return 'recycling';
        if (treatmentMethod.includes('Incineration')) return 'incineration';
        if (treatmentMethod.includes('Composting')) return 'composting';
        if (treatmentMethod.includes('Hazardous')) return 'hazardous';
        return 'landfill';
    };

    const calculateWasteEmissions = async (entry, index) => {
        try {
            let quantity = 0;

            switch (inputMethod) {
                case 'quantity':
                    quantity = parseFloat(entry.quantity) || 0;
                    if (entry.quantityUnit === 'kg') quantity = quantity / 1000;
                    break;
                case 'bins':
                    quantity = calculateQuantityFromBins(entry);
                    break;
                case 'spend':
                    quantity = parseFloat(entry.annualSpend) / 200;
                    break;
            }

            if (quantity <= 0 || isNaN(quantity)) {
                throw new Error(`Invalid quantity for ${entry.wasteType}`);
            }

            const factorKey = `${entry.wasteType}_${entry.treatmentMethod}`;
            const emissionFactor = state.availableFactors?.[factorKey];

            let co2e = 0;
            let calculationMethod = 'default_factor';

            if (emissionFactor) {
                const requestBody = {
                    emission_factor: {
                        activity_id: emissionFactor.activity_id,
                        source: emissionFactor.source,
                        region: emissionFactor.region,
                        year: parseInt(emissionFactor.year),
                        data_version: "^3"
                    },
                    parameters: {
                        weight: quantity,
                        weight_unit: "t"
                    }
                };

                const response = await fetch('https://api.climatiq.io/data/v1/estimate', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody)
                });

                if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                const data = await response.json();
                co2e = data.co2e;
                calculationMethod = 'climatiq_api';
            } else {
                const defaultFactor = defaultWasteEmissionFactors[entry.wasteType];
                if (defaultFactor) {
                    const treatmentKey = getTreatmentKey(entry.treatmentMethod);
                    co2e = quantity * (defaultFactor[treatmentKey] || defaultFactor.landfill);
                } else {
                    co2e = quantity * 500;
                }
            }

            if (entry.isReused && entry.reuseQuantity) {
                const reuseQuantity = parseFloat(entry.reuseQuantity) || 0;
                co2e -= reuseQuantity * 200;
            }

            return {
                co2e,
                co2e_unit: 'kg',
                calculation_method: calculationMethod,
                quantity,
                quantity_unit: 't'
            };
        } catch (error) {
            console.error(`Error calculating emissions for waste entry ${index}:`, error);
            throw error;
        }
    };

    const validateWasteEntries = () => {
        const errors = [];
        wasteEntries.forEach((entry, index) => {
            if (!entry.site) errors.push(`Entry ${index + 1}: Please enter a site name`);
            if (!entry.wasteType) errors.push(`Entry ${index + 1}: Please select a waste type`);
            if (!entry.treatmentMethod) errors.push(`Entry ${index + 1}: Please select a treatment method`);

            switch (inputMethod) {
                case 'quantity':
                    if (!entry.quantity) errors.push(`Entry ${index + 1}: Please enter quantity`);
                    break;
                case 'bins':
                    if (!entry.containerSize || !entry.collectionsPerYear) {
                        errors.push(`Entry ${index + 1}: Please complete bin/container details`);
                    }
                    break;
                case 'spend':
                    if (!entry.annualSpend || !entry.serviceDescription) {
                        errors.push(`Entry ${index + 1}: Please complete spend details`);
                    }
                    break;
            }
        });

        if (!formData.finalConfirmation) {
            errors.push('Please confirm you have entered all major waste streams');
        }

        return errors;
    };

    const handleCalculate = async () => {
        if (wasteEntries.length === 0) {
            toastr.error('Please add at least one waste entry');
            return;
        }

        const validationErrors = validateWasteEntries();
        if (validationErrors.length > 0) {
            validationErrors.forEach(error => toastr.error(error));
            return;
        }

        updateState({ isCalculating: true, calculationResults: {} });
        toastr.info('Calculating waste emissions...');

        try {
            const results = {};
            let hasSuccessfulCalculation = false;

            for (let i = 0; i < wasteEntries.length; i++) {
                const entry = wasteEntries[i];

                try {
                    const apiResult = await calculateWasteEmissions(entry, i);
                    const resultKey = `waste_${i}`;

                    results[resultKey] = {
                        site: entry.site,
                        wasteType: entry.wasteType,
                        treatmentMethod: entry.treatmentMethod,
                        quantity: apiResult.quantity,
                        quantity_unit: apiResult.quantity_unit,
                        co2e: apiResult.co2e,
                        co2e_unit: apiResult.co2e_unit,
                        calculation_method: apiResult.calculation_method,
                        isHazardous: entry.isHazardous,
                        isOrganic: entry.isOrganic,
                        isReused: entry.isReused
                    };

                    toastr.success(`Calculated emissions for ${entry.wasteType}: ${apiResult.co2e.toFixed(2)} kg CO₂e`);
                    hasSuccessfulCalculation = true;
                } catch (error) {
                    console.error(`Calculation error for waste entry ${i + 1}:`, error);
                    toastr.error(`Failed to calculate emissions for entry ${i + 1}: ${error.message}`);
                }
            }

            updateState({ calculationResults: results });

            if (hasSuccessfulCalculation) {
                const totalEmissions = Object.values(results).reduce((sum, result) => sum + (result.co2e || 0), 0);
                const totalWaste = Object.values(results).reduce((sum, result) => sum + (result.quantity || 0), 0);
                toastr.success(`Calculation complete! Total: ${totalEmissions.toFixed(2)} kg CO₂e from ${totalWaste.toFixed(2)} tonnes waste`);
            }
        } catch (error) {
            toastr.error('Calculation failed. Please check your inputs and try again.');
            console.error('Calculation error:', error);
        } finally {
            updateState({ isCalculating: false });
        }
    };

    const handleAddEntry = () => {
        const newEntries = [...wasteEntries, {
            site: '', period: formData.year || new Date().getFullYear().toString(), wasteType: '', treatmentMethod: '',
            quantity: '', quantityUnit: 't',
            containerSize: '', collectionsPerYear: '', fillLevel: 1,
            annualSpend: '', serviceDescription: '', vendorName: '',
            isHazardous: false, hazardousDetails: '',
            isOrganic: false, moisturePercentage: '',
            isReused: false, reuseDescription: '', reuseQuantity: '',
            evidence: null
        }];
        updateState({ wasteEntries: newEntries });
    };

    const handleEntryChange = (index, field, value) => {
        const updated = [...wasteEntries];
        updated[index][field] = value;
        updateState({ wasteEntries: updated });
    };

    const handleRemoveEntry = (index) => {
        updateState({ wasteEntries: wasteEntries.filter((_, i) => i !== index) });
    };

    const loadSampleData = () => {
        updateState({
            formData: { ...formData, year: '2025', finalConfirmation: 'yes' },
            wasteEntries: [{
                site: 'Main Office', period: '2025', wasteType: 'Mixed municipal',
                treatmentMethod: 'Landfill (managed)', quantity: '25', quantityUnit: 't',
                isHazardous: false, isOrganic: false, isReused: false, evidence: null
            }]
        });
        toastr.info('Sample data loaded. Click "Search Factors" to find emission factors.');
    };

    const totalWaste = wasteEntries.reduce((sum, entry) => {
        let quantity = 0;
        switch (inputMethod) {
            case 'quantity':
                quantity = parseFloat(entry.quantity) || 0;
                if (entry.quantityUnit === 'kg') quantity = quantity / 1000;
                break;
            case 'bins':
                quantity = calculateQuantityFromBins(entry);
                break;
            case 'spend':
                quantity = parseFloat(entry.annualSpend) / 200 || 0;
                break;
        }
        return sum + quantity;
    }, 0);

    const totalEmissions = Object.values(state.calculationResults || {}).reduce((sum, result) => sum + (result.co2e || 0), 0);

    return (
        <div className="max-w-6xl mx-auto px-4 py-8 pb-24">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-8">
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                            <strong>API Status:</strong> {CLIMATIQ_API_KEY && CLIMATIQ_API_KEY !== 'Y2ZBNWZKRzRGNzJYNjRQMzpa' ?
                                '✅ API Key Configured' : '❌ Please set REACT_APP_CLIMATIQ_API_KEY in .env file'}
                        </p>
                    </div>

                    <div className="flex justify-end mb-6">
                        <button
                            onClick={loadSampleData}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 text-sm"
                        >
                            Load Sample Data
                        </button>
                    </div>

                    {(wasteEntries.length > 0 || Object.keys(state.calculationResults || {}).length > 0) && (
                        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <h3 className="text-sm font-semibold text-blue-800">Total Waste</h3>
                                <p className="text-2xl font-bold text-blue-600">{totalWaste.toFixed(1)} tonnes</p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                <h3 className="text-sm font-semibold text-green-800">Total Emissions</h3>
                                <p className="text-2xl font-bold text-green-600">{totalEmissions.toFixed(0)} kg CO₂e</p>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                                <h3 className="text-sm font-semibold text-purple-800">Entries</h3>
                                <p className="text-2xl font-bold text-purple-600">{wasteEntries.length}</p>
                            </div>
                        </div>
                    )}

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Reporting Year *</label>
                        <select
                            value={formData.year || new Date().getFullYear().toString()}
                            onChange={(e) => updateState({ formData: { ...formData, year: e.target.value } })}
                            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            {Array.from({ length: new Date().getFullYear() - 2009 }, (_, i) =>
                                (new Date().getFullYear() - i).toString()
                            ).map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">How would you like to enter waste data?</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <button
                                onClick={() => updateState({ inputMethod: 'quantity' })}
                                className={`p-4 rounded-lg border-2 transition-all ${inputMethod === 'quantity'
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                <div className="font-semibold">Direct Quantity</div>
                                <div className="text-sm mt-1">Enter tonnes or kilograms directly</div>
                            </button>
                            <button
                                onClick={() => updateState({ inputMethod: 'bins' })}
                                className={`p-4 rounded-lg border-2 transition-all ${inputMethod === 'bins'
                                        ? 'border-green-500 bg-green-50 text-green-700'
                                        : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                <div className="font-semibold">Bins/Containers</div>
                                <div className="text-sm mt-1">Based on container size and collections</div>
                            </button>
                            <button
                                onClick={() => updateState({ inputMethod: 'spend' })}
                                className={`p-4 rounded-lg border-2 transition-all ${inputMethod === 'spend'
                                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                                        : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                <div className="font-semibold">Annual Spend</div>
                                <div className="text-sm mt-1">Based on waste service costs</div>
                            </button>
                        </div>
                    </div>

                    <div className="mb-6">
                        <button
                            onClick={handleSearchFactors}
                            disabled={state.isSearching}
                            className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isSearching ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaSearch className="w-4 h-4" />
                            )}
                            <span>{state.isSearching ? 'Searching...' : 'Search Emission Factors'}</span>
                        </button>
                    </div>

                    {Object.keys(state.availableFactors || {}).length > 0 && (
                        <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                            <h3 className="font-semibold text-green-800 mb-2">Available Waste Emission Factors</h3>
                            <div className="space-y-2">
                                {Object.entries(state.availableFactors).map(([key, factor]) => (
                                    <div key={key} className="text-sm text-green-700">
                                        <strong>{key.split('_')[0]}:</strong> {factor.name} ({factor.source}, {factor.region})
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">8.2 Waste Generated and Treatment</h3>

                        <button
                            onClick={handleAddEntry}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 mb-6"
                        >
                            <FaPlus className="w-4 h-4" />
                            <span>Add Waste Entry</span>
                        </button>

                        {wasteEntries.map((entry, index) => {
                            const resultKey = `waste_${index}`;
                            const result = state.calculationResults?.[resultKey];

                            return (
                                <div key={index} className="p-6 border-2 border-gray-200 rounded-lg space-y-4 mb-4">
                                    <div className="flex justify-between items-center">
                                        <h4 className="font-semibold">Waste Entry {index + 1}</h4>
                                        <button
                                            onClick={() => handleRemoveEntry(index)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <FaTrashAlt className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {result && (
                                        <div className="p-3 bg-green-100 rounded-lg border border-green-200">
                                            <p className="text-sm text-green-800 font-medium">
                                                Emissions: {result.co2e.toFixed(2)} {result.co2e_unit}
                                            </p>
                                            <p className="text-xs text-green-600">
                                                {result.quantity.toFixed(2)} {result.quantity_unit} of {entry.wasteType}
                                            </p>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Site Name/ID *</label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="e.g., Main Office"
                                                value={entry.site}
                                                onChange={(e) => handleEntryChange(index, 'site', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Reporting Period</label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="e.g., 2024"
                                                value={entry.period}
                                                onChange={(e) => handleEntryChange(index, 'period', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Waste Type *</label>
                                            <select
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                value={entry.wasteType}
                                                onChange={(e) => handleEntryChange(index, 'wasteType', e.target.value)}
                                            >
                                                <option value="">Select waste type</option>
                                                <optgroup label="Non-Hazardous">
                                                    {wasteCategories.nonHazardous.map(type => (
                                                        <option key={type} value={type}>{type}</option>
                                                    ))}
                                                </optgroup>
                                                <optgroup label="Hazardous">
                                                    {wasteCategories.hazardous.map(type => (
                                                        <option key={type} value={type}>{type}</option>
                                                    ))}
                                                </optgroup>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Treatment Method *</label>
                                            <select
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                value={entry.treatmentMethod}
                                                onChange={(e) => handleEntryChange(index, 'treatmentMethod', e.target.value)}
                                            >
                                                <option value="">Select treatment method</option>
                                                {treatmentMethods.map(method => (
                                                    <option key={method} value={method}>{method}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {inputMethod === 'quantity' && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                                                <div className="flex space-x-2">
                                                    <input
                                                        type="number"
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="Enter quantity"
                                                        value={entry.quantity}
                                                        onChange={(e) => handleEntryChange(index, 'quantity', e.target.value)}
                                                    />
                                                    <select
                                                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                                                        value={entry.quantityUnit}
                                                        onChange={(e) => handleEntryChange(index, 'quantityUnit', e.target.value)}
                                                    >
                                                        <option value="t">tonnes</option>
                                                        <option value="kg">kg</option>
                                                    </select>
                                                </div>
                                            </div>
                                        )}

                                        {inputMethod === 'bins' && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Container Size/Type *</label>
                                                    <select
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        value={entry.containerSize}
                                                        onChange={(e) => handleEntryChange(index, 'containerSize', e.target.value)}
                                                    >
                                                        <option value="">Select container</option>
                                                        {containerSizes.map(size => (
                                                            <option key={size} value={size}>{size}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Collections Per Year *</label>
                                                    <input
                                                        type="number"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="e.g., 52"
                                                        value={entry.collectionsPerYear}
                                                        onChange={(e) => handleEntryChange(index, 'collectionsPerYear', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Typical Fill Level</label>
                                                    <select
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        value={entry.fillLevel}
                                                        onChange={(e) => handleEntryChange(index, 'fillLevel', parseFloat(e.target.value))}
                                                    >
                                                        {fillLevels.map(level => (
                                                            <option key={level.value} value={level.value}>{level.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </>
                                        )}

                                        {inputMethod === 'spend' && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Annual Spend *</label>
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-gray-600">$</span>
                                                        <input
                                                            type="number"
                                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                            placeholder="Enter amount"
                                                            value={entry.annualSpend}
                                                            onChange={(e) => handleEntryChange(index, 'annualSpend', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Service Description *</label>
                                                    <input
                                                        type="text"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="e.g., mixed municipal to landfill"
                                                        value={entry.serviceDescription}
                                                        onChange={(e) => handleEntryChange(index, 'serviceDescription', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name (Optional)</label>
                                                    <input
                                                        type="text"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="Vendor company name"
                                                        value={entry.vendorName}
                                                        onChange={(e) => handleEntryChange(index, 'vendorName', e.target.value)}
                                                    />
                                                </div>
                                            </>
                                        )}

                                        <div className="md:col-span-2 space-y-4 p-4 bg-gray-50 rounded-lg">
                                            <h5 className="font-semibold text-gray-700">Special Questions</h5>

                                            <div className="space-y-3">
                                                <label className="flex items-start space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={entry.isHazardous || false}
                                                        onChange={(e) => handleEntryChange(index, 'isHazardous', e.target.checked)}
                                                        className="w-5 h-5 mt-1"
                                                    />
                                                    <div>
                                                        <span>Is this waste hazardous?</span>
                                                        {entry.isHazardous && (
                                                            <input
                                                                type="text"
                                                                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                                                placeholder="Manifest ID or permit number"
                                                                value={entry.hazardousDetails || ''}
                                                                onChange={(e) => handleEntryChange(index, 'hazardousDetails', e.target.value)}
                                                            />
                                                        )}
                                                    </div>
                                                </label>

                                                <label className="flex items-start space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={entry.isOrganic || false}
                                                        onChange={(e) => handleEntryChange(index, 'isOrganic', e.target.checked)}
                                                        className="w-5 h-5 mt-1"
                                                    />
                                                    <div>
                                                        <span>Is the waste organic/biomass?</span>
                                                        {entry.isOrganic && (
                                                            <input
                                                                type="number"
                                                                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                                                placeholder="Moisture % (if known)"
                                                                value={entry.moisturePercentage || ''}
                                                                onChange={(e) => handleEntryChange(index, 'moisturePercentage', e.target.value)}
                                                            />
                                                        )}
                                                    </div>
                                                </label>

                                                <label className="flex items-start space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={entry.isReused || false}
                                                        onChange={(e) => handleEntryChange(index, 'isReused', e.target.checked)}
                                                        className="w-5 h-5 mt-1"
                                                    />
                                                    <div>
                                                        <span>Was any material reused on-site?</span>
                                                        {entry.isReused && (
                                                            <div className="mt-1 space-y-2">
                                                                <input
                                                                    type="text"
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                                                    placeholder="Briefly describe the reuse"
                                                                    value={entry.reuseDescription || ''}
                                                                    onChange={(e) => handleEntryChange(index, 'reuseDescription', e.target.value)}
                                                                />
                                                                <input
                                                                    type="number"
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                                                    placeholder="Reuse quantity (tonnes)"
                                                                    value={entry.reuseQuantity || ''}
                                                                    onChange={(e) => handleEntryChange(index, 'reuseQuantity', e.target.value)}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </label>
                                            </div>
                                        </div>

                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Evidence Upload</label>
                                            <input
                                                type="file"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                onChange={(e) => handleEntryChange(index, 'evidence', e.target.files[0])}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mb-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">8.6 Final Confirmation</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    For each site, please confirm you have entered all major waste streams for the year:
                                </label>
                                <div className="flex space-x-4">
                                    <button
                                        onClick={() => updateState({ formData: { ...formData, finalConfirmation: 'yes' } })}
                                        className={`px-6 py-3 rounded-lg border-2 transition-all ${formData.finalConfirmation === 'yes'
                                                ? 'border-green-500 bg-green-500 text-white hover:bg-green-600'
                                                : 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        Yes
                                    </button>
                                    <button
                                        onClick={() => updateState({ formData: { ...formData, finalConfirmation: 'no' } })}
                                        className={`px-6 py-3 rounded-lg border-2 transition-all ${formData.finalConfirmation === 'no'
                                                ? 'border-red-500 bg-red-500 text-white hover:bg-red-600'
                                                : 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        No
                                    </button>
                                </div>
                            </div>

                            {formData.finalConfirmation === 'no' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Briefly say what is missing and why:
                                    </label>
                                    <textarea
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                        rows="3"
                                        placeholder="e.g., Construction waste data not available..."
                                        value={formData.missingInfo || ''}
                                        onChange={(e) => updateState({ formData: { ...formData, missingInfo: e.target.value } })}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {Object.keys(state.calculationResults || {}).length > 0 && (
                        <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Calculation Results Summary</h3>
                            <div className="space-y-3">
                                {Object.entries(state.calculationResults).map(([key, result]) => (
                                    <div key={key} className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
                                        <div>
                                            <span className="font-medium text-gray-700">
                                                {result.site} - {result.wasteType}
                                            </span>
                                            <p className="text-sm text-gray-500">
                                                {result.quantity.toFixed(2)} {result.quantity_unit} • {result.treatmentMethod}
                                            </p>
                                        </div>
                                        <span className="font-semibold text-green-700">
                                            {result.co2e.toFixed(2)} {result.co2e_unit}
                                        </span>
                                    </div>
                                ))}
                                <div className="border-t border-gray-300 pt-3 mt-2">
                                    <div className="flex justify-between items-center font-bold text-lg">
                                        <span>Total Waste Emissions:</span>
                                        <span className="text-blue-700">
                                            {totalEmissions.toFixed(2)} kg CO₂e
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            onClick={handleCalculate}
                            disabled={state.isCalculating}
                            className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isCalculating ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaCalculator className="w-4 h-4" />
                            )}
                            <span>{state.isCalculating ? 'Calculating...' : 'Calculate'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Api8Form;